import { useEffect, useRef } from 'react';
import { Button, Tooltip } from 'antd';
import type { ButtonProps } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';

/**
 * ExportButton
 * ----------------------------------------------------------------------------
 * Triggers the browser's native print dialog with a meaningful suggested
 * filename. Users choose "Save as PDF" as the destination to export the
 * current view.
 */

export interface ExportButtonProps {
  /**
   *  Do NOT include the .pdf extension — browsers add this automatically.
   */
  filename: () => string;
  onBeforePrint?: () => void | Promise<void>;
  onAfterPrint?: () => void;
  prePrintDelayMs?: number;
  label?: string;
  disabled?: boolean;
  buttonProps?: Omit<ButtonProps, 'icon' | 'onClick' | 'disabled' | 'children'>;
}

export function ExportButton({
  filename,
  onBeforePrint,
  onAfterPrint,
  prePrintDelayMs = 100,
  label = 'Export PDF',
  disabled = false,
  buttonProps,
}: ExportButtonProps) {
  const originalTitleRef = useRef<string>('');

  // Wire up afterprint listener once. Using a ref to track the original title
  // ensures the listener always restores the right value across multiple
  // export clicks.
  useEffect(() => {
    const handleAfterPrint = () => {
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current;
        originalTitleRef.current = '';
      }
      onAfterPrint?.();
    };

    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [onAfterPrint]);

  const handleClick = async () => {
    if (disabled) return;
    originalTitleRef.current = document.title;
    const generatedName = filename().replace(/\.pdf$/i, '');
    document.title = generatedName;

    try {
      await Promise.resolve(onBeforePrint?.());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('ExportButton: onBeforePrint threw', err);
    }

    if (prePrintDelayMs > 0) {
      await new Promise(resolve => setTimeout(resolve, prePrintDelayMs));
    }

    window.print();
  };

  const button = (
    <Button
      type="default"
      icon={<FilePdfOutlined />}
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      className="no-print"
      {...buttonProps}
    >
      {label}
    </Button>
  );

  if (disabled) {
    return (
      <Tooltip title="Loading data — please wait">
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
}

export default ExportButton;
